import { ActionIcon, Avatar, Box, Button, Group, LoadingOverlay, Select, Stack, Table, Text } from "@mantine/core"
import { useForm } from "@mantine/form"
import { ImageIcon, TrashIcon } from "@phosphor-icons/react"
import { yupResolver } from "mantine-form-yup-resolver"
import React from "react"
import { useTranslation } from "react-i18next"
import * as yup from "yup"
import { i18n } from "@dehub/theme"
import { DropShippingApi } from "../../apis"
import HttpHandler from "../../utils/http-handler"
import { message } from "../../utils/message"
import { renderBusinessName } from "../../utils/render-business-name"
import { usePermission } from "../permission"
import { ReasonTooltip } from "../ui"

const schema = yup.object({
  lines: yup.array().of(
    yup.object({
      quantity: yup.string().required(() => i18n.t("common:validation.required")).nullable(),
      buyerBusinessId: yup.string().required(() => i18n.t("common:validation.required")).nullable(),
      supplierBusinessId: yup.string().required(() => i18n.t("common:validation.required")).nullable(),
    }),
  ),
})

type IFormLine = { quantity?: string; variantId?: string; buyerBusinessId?: string; supplierBusinessId?: string }
export type IFormData = { lines: IFormLine[] }

type Item = {
  id?: string
  variantId?: string
  image?: string | null
  skuCode?: string | null
  name?: string | null
  unit?: string | null
  quantity?: number | string | null
  buyers?: { id: string; isDefault?: boolean }[]
  suppliers?: { id: string; isDefault?: boolean; buyerBusinessId?: string; availableQuantity?: number }[]
}

/**
 * Дропшиппинг захиалга үүсгэх (Ord_DropShipDetail). The caller shows it in a 720 drawer. Removing a row removes the
 * item from the table AND from the submitted lines (both are indexed by the same position). The BE needs ORD_DRSHP
 * (order/dropshipping/create.ts).
 */
export function SupplierSalesShippingForm({
  action,
  isDropshipping,
  onCancel,
  onSuccess,
}: {
  action: string
  isDropshipping: any
  onCancel: () => void
  onSuccess: () => void
}) {
  const { t } = useTranslation(["supplierSales", "common"])
  const ORD_DRSHP = usePermission("ORD_DRSHP")
  const [loading, setLoading] = React.useState<boolean>(false)
  const [items, setItems] = React.useState<Item[]>(isDropshipping?.[1] || [])

  const form = useForm<IFormData>({
    mode: "controlled",
    initialValues: {
      lines: (isDropshipping?.[1] || []).map((item: Item) => ({
        quantity: item?.quantity as string,
        variantId: item?.variantId,
        buyerBusinessId: (item?.buyers || []).find((b) => b.isDefault)?.id,
        supplierBusinessId: (item?.suppliers || []).find((s) => s.isDefault)?.id,
      })),
    },
    validate: yupResolver(schema),
  })

  const onRemoveRow = (index: number) => {
    setItems((rows) => rows.filter((_, i) => i !== index))
    form.setFieldValue("lines", form.values.lines.filter((_, i) => i !== index))
  }

  const onSubmit = async () => {
    const { hasErrors } = form.validate()
    if (hasErrors) return
    setLoading(true)
    try {
      await DropShippingApi.create({
        orderId: action,
        lines: form.values.lines.map((l) => ({ variantId: l.variantId, buyerBusinessId: l.buyerBusinessId, supplierBusinessId: l.supplierBusinessId })) as never,
      })
      message.success(t("common:messages.requestSuccess"))
      onSuccess()
    } catch (err) {
      message.error((err as HttpHandler)?.message!)
    }
    setLoading(false)
  }

  return (
    <Box pos="relative">
      <LoadingOverlay visible={loading} />
      <Stack gap="md">
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{t("supplierSales:sales.name")}</Table.Th>
              <Table.Th ta="right">{t("supplierSales:sales.orderQuantity")}</Table.Th>
              <Table.Th>{t("supplierSales:sales.buyerName")}</Table.Th>
              <Table.Th>{t("supplierSales:sales.supplierName")}</Table.Th>
              <Table.Th>{t("supplierSales:sales.supplierBalance")}</Table.Th>
              <Table.Th>{t("common:table.action")}</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {items.map((record, index) => {
              const line = form.values.lines[index]
              const supplier = (record.suppliers || []).find((s) => s.id === line?.supplierBusinessId)
              const available = (supplier?.availableQuantity ?? 0) >= Number(record.quantity ?? 0)
              return (
                <Table.Tr key={record.id ?? index}>
                  <Table.Td>
                    <Group gap="sm" wrap="nowrap">
                      <Avatar size={40} src={record.image || null} radius="sm"><ImageIcon /></Avatar>
                      <Box miw={0}>
                        <Text fz={14} fw={600} lineClamp={2}>{record.name || "-"}</Text>
                        <Text fz={14} c="gray.8" ff="monospace">{record.skuCode || "-"}</Text>
                      </Box>
                    </Group>
                  </Table.Td>
                  <Table.Td ta="right"><Text fz={14} ff="monospace">{record.quantity ?? "-"}</Text></Table.Td>
                  <Table.Td>
                    <Select
                      value={line?.buyerBusinessId ?? null}
                      onChange={(v) => {
                        form.setFieldValue(`lines.${index}.buyerBusinessId`, v ?? undefined)
                        form.setFieldValue(`lines.${index}.supplierBusinessId`, undefined)
                      }}
                      placeholder={t("common:actions.select")}
                      data={(record.buyers || []).map((b) => ({ value: b.id, label: renderBusinessName(b as never, "") }))}
                      error={form.errors[`lines.${index}.buyerBusinessId`]}
                      w={180}
                    />
                  </Table.Td>
                  <Table.Td>
                    <Select
                      value={line?.supplierBusinessId ?? null}
                      onChange={(v) => form.setFieldValue(`lines.${index}.supplierBusinessId`, v ?? undefined)}
                      placeholder={t("common:actions.select")}
                      data={(record.suppliers || [])
                        .filter((s) => s.buyerBusinessId === line?.buyerBusinessId)
                        .map((s) => ({ value: s.id, label: renderBusinessName(s as never, "") }))}
                      disabled={!line?.buyerBusinessId}
                      error={form.errors[`lines.${index}.supplierBusinessId`]}
                      w={180}
                    />
                  </Table.Td>
                  <Table.Td>
                    <Text fz={14} fw={600}>{supplier ? (available ? t("supplierSales:sales.available") : t("supplierSales:sales.unavailable")) : "-"}</Text>
                  </Table.Td>
                  <Table.Td>
                    <ActionIcon variant="light" color="gray" size={32} radius="md" aria-label={t("common:actions.delete")} onClick={() => onRemoveRow(index)}>
                      <TrashIcon size={18} />
                    </ActionIcon>
                  </Table.Td>
                </Table.Tr>
              )
            })}
          </Table.Tbody>
        </Table>

        <Group justify="flex-end" gap="sm">
          <Button variant="default" onClick={() => onCancel?.()}>{t("common:actions.dismiss")}</Button>
          <ReasonTooltip reason={ORD_DRSHP.reason ?? (items.length === 0 ? t("supplierSales:sales.selectLinesFirst") : null)}>
            <Button onClick={onSubmit}>{t("supplierSales:dropshipping.createSubmit")}</Button>
          </ReasonTooltip>
        </Group>
      </Stack>
    </Box>
  )
}
